/**
 * Local Events at Places — data layer (Phase 1 MVP).
 *
 * Mirrors the pattern of `useBlogs.ts` / `useCounts.ts`: TanStack Query hooks
 * for reads, mutations for writes, keys registered in `queryKeys.events`.
 *
 * All endpoints hang off `/api/events` (see `nodejs_server/controllers/
 * EventsController.js`). The server response is `{ event }` for single-event
 * shapes, `{ events, radius_km? }` for lists, `{ attendees, page, limit }`
 * for the paginated attendee endpoint. The API client (`api`) already unwraps
 * the envelope, so the hooks return the inner payload directly.
 */
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { api } from '@/lib/api/client';
import { queryKeys } from '@/lib/api/queryKeys';
import { useAuth } from '@/pages/auth';
import type {
  CreatePlaceEventInput,
  DiscoverEventsParams,
  DiscoverEventsResult,
  EventChatList,
  EventChatMessage,
  EventWeather,
  MyEventsResult,
  NearbyEventsResult,
  PlaceEvent,
  PlaceEventAttendee,
  PlaceEventEnvelope,
  RsvpStatus,
} from '@/types/api';


/** Encode object params to a `?a=1&b=2` string, skipping empty values. */
function toQuery(params: Record<string, unknown> = {}): string {
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    search.set(k, String(v));
  }
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

/** GET /api/events/:id — one event (public when visibility='P'). */
export function useEvent(id: number | string | undefined) {
  return useQuery({
    queryKey: queryKeys.events.detail(id ?? ''),
    enabled: id !== undefined && id !== null && id !== '',
    staleTime: 30_000,
    queryFn: () => api.get<PlaceEventEnvelope>(`events/${id}`).then(r => r.event),
  });
}

/** GET /api/events/nearby — public discovery around a coordinate. */
export interface NearbyEventsParams {
  lat: number | null | undefined;
  lng: number | null | undefined;
  radiusKm?: number;
  limit?: number;
  /** Optional category filter — mirrors the server's `?category=` param
   *  on `/api/events/nearby`. Lets the "Events in <city>" rail stay
   *  useful when the user has picked e.g. Music — the rail then shows
   *  music events near them instead of hiding entirely. */
  category?: string | null;
}
export function useNearbyEvents({ lat, lng, radiusKm = 25, limit = 25, category = null }: NearbyEventsParams) {
  const enabled = lat != null && lng != null && !Number.isNaN(lat) && !Number.isNaN(lng);
  return useQuery({
    queryKey: queryKeys.events.nearby({ lat, lng, radiusKm, limit, category: category ?? null }),
    enabled,
    staleTime: 30_000,
    queryFn: () =>
      api.get<NearbyEventsResult>(
        `events/nearby${toQuery({ lat, lng, radius_km: radiusKm, limit, category: category || undefined })}`,
      ),
  });
}

/** GET /api/events/mine — auth user's created + RSVPed upcoming events. */
export function useMyEvents(enabled = true) {
  // Scope the cache by viewer ID so a logout → login swap doesn't surface
  // the previous account's events (TanStack cache keyed on `queryKey`; a
  // shared key leaks across users).
  const userId = useAuth((s) => s.user?.ID ?? null);
  return useQuery({
    queryKey: queryKeys.events.mine(userId),
    enabled: enabled && !!userId,
    staleTime: 15_000,
    queryFn: () => api.get<MyEventsResult>('events/mine'),
  });
}

/** GET /api/events/:id/attendees — paginated attendee list. */
export function useEventAttendees(
  id: number | string | undefined,
  { page = 1, limit = 20, status = 'G' as 'G' | 'M' | 'GM' | 'ALL' } = {},
) {
  return useQuery({
    queryKey: queryKeys.events.attendees(id ?? '', { page, limit, status }),
    enabled: id !== undefined && id !== null && id !== '',
    staleTime: 30_000,
    queryFn: () =>
      api.get<{ attendees: PlaceEventAttendee[]; page: number; limit: number }>(
        `events/${id}/attendees${toQuery({ page, limit, status })}`,
      ),
  });
}

/** POST /api/events — create. Invalidates nearby + mine on success.
 *
 * Accepts either a plain `CreatePlaceEventInput` (JSON body) or a `FormData`
 * instance (multipart, used when the user attaches a `cover_image` file).
 * The API client at `@/lib/api/client` detects FormData and skips the
 * `Content-Type: application/json` header so the browser can add the
 * multipart boundary. See `nodejs_server/app.js` — `/api/events` runs multer
 * before the router when the request is multipart. */
export function useCreateEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreatePlaceEventInput | FormData) =>
      api.post<PlaceEventEnvelope>('events', payload).then(r => r.event),
    onSuccess: (event: PlaceEvent) => {
      // Seed the detail cache so navigation to /event/:id is instant.
      qc.setQueryData(queryKeys.events.detail(event.id), event);
      qc.invalidateQueries({ queryKey: queryKeys.events.all });
    },
  });
}

/** PATCH /api/events/:id — update (creator only). */
export function useUpdateEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: number | string; patch: Partial<CreatePlaceEventInput> }) =>
      api.patch<PlaceEventEnvelope>(`events/${id}`, patch).then(r => r.event),
    onSuccess: (event: PlaceEvent) => {
      qc.setQueryData(queryKeys.events.detail(event.id), event);
      qc.invalidateQueries({ queryKey: queryKeys.events.all });
    },
  });
}

/** DELETE /api/events/:id — soft delete (creator only). */
export function useDeleteEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number | string) => api.delete<unknown>(`events/${id}`),
    onSuccess: (_data, id) => {
      qc.removeQueries({ queryKey: queryKeys.events.detail(id) });
      qc.invalidateQueries({ queryKey: queryKeys.events.all });
    },
  });
}

/** POST /api/events/:id/rsvp — set/change RSVP.
 *  Pass status=null to remove the RSVP entirely (DELETE /rsvp). */
export function useRsvp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      status,
    }: {
      id: number | string;
      status: RsvpStatus | null;
    }) => {
      if (status === null) {
        await api.delete<unknown>(`events/${id}/rsvp`);
        return null;
      }
      const r = await api.post<PlaceEventEnvelope>(`events/${id}/rsvp`, { status });
      return r.event;
    },
    onSuccess: (event, vars) => {
      qc.invalidateQueries({ queryKey: queryKeys.events.detail(vars.id) });
      qc.invalidateQueries({ queryKey: queryKeys.events.all });
      if (event) qc.setQueryData(queryKeys.events.detail(event.id), event);
    },
  });
}

/**
 * GET /api/events/discover — public upcoming-events feed with filters.
 *
 * Uses `useInfiniteQuery` so the discovery page can scroll through pages
 * via a `next_cursor` the server emits (the ISO starts_at of the last
 * row on the current page).
 */
export function useDiscoverEvents(params: Omit<DiscoverEventsParams, 'cursor'> = {}) {
  const stableParams = {
    range: params.range ?? 'all',
    category: params.category ?? null,
    q: params.q ?? '',
    limit: params.limit ?? 20,
    country: params.country ?? null,
    city: params.city ?? null,
    from: params.from ?? null,
    to: params.to ?? null,
  };
  return useInfiniteQuery({
    queryKey: queryKeys.events.discover(stableParams),
    initialPageParam: null as string | null,
    staleTime: 30_000,
    queryFn: ({ pageParam }) => {
      const search = new URLSearchParams();
      search.set('range', stableParams.range);
      if (stableParams.category) search.set('category', stableParams.category);
      if (stableParams.q) search.set('q', stableParams.q);
      if (stableParams.country) search.set('country', stableParams.country);
      if (stableParams.city) search.set('city', stableParams.city);
      if (stableParams.from) search.set('from', stableParams.from);
      if (stableParams.to) search.set('to', stableParams.to);
      search.set('limit', String(stableParams.limit));
      if (pageParam) search.set('cursor', String(pageParam));
      return api.get<DiscoverEventsResult>(`events/discover?${search.toString()}`);
    },
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });
}

/** GET /api/events/category-counts — per-category event tallies for the
 *  discover-page chip row. Scoped by `country` just like discover, so the
 *  chip order reflects what the viewer will actually see when they click.
 *  Server caches 120s; client stale 60s so a filter switch returns fast. */
export function useCategoryCounts(country: string | null | undefined) {
  const key = country || '';
  return useQuery<{ counts: Record<string, number> }>({
    queryKey: [...queryKeys.events.all, 'category-counts', key] as const,
    staleTime: 60_000,
    queryFn: () =>
      api.get<{ counts: Record<string, number> }>(
        `events/category-counts${toQuery({ country: country || undefined })}`,
      ),
  });
}

/** GET /api/events/:id/related — related events (same category + fallback
 *  from city → country → nearby). Public, 5m stale, server caches 5m. */
export function useRelatedEvents(id: number | string | undefined, limit = 6) {
  return useQuery<{
    events: import('@/types/api').PlaceEvent[];
    source: 'city' | 'city+country' | 'country' | 'city+country+nearby' | 'country+nearby' | 'nearby' | 'none';
    category: string | null;
    city: string | null;
    country: string | null;
  }>({
    queryKey: queryKeys.events.related(id ?? ''),
    enabled: id !== undefined && id !== null && id !== '',
    staleTime: 5 * 60 * 1000,
    queryFn: () => api.get(`events/${id}/related?limit=${limit}`),
  });
}

/** GET /api/events/:id/weather — weather badge. Public, 5m stale. */
export function useEventWeather(id: number | string | undefined) {
  return useQuery({
    queryKey: queryKeys.events.weather(id ?? ''),
    enabled: id !== undefined && id !== null && id !== '',
    staleTime: 5 * 60 * 1000,
    retry: false,
    queryFn: () =>
      api
        .get<{ weather: EventWeather | null }>(`events/${id}/weather`)
        .then((r) => r.weather),
  });
}

/** GET /api/events/:id/chat — event chat room messages. */
export function useEventChat(id: number | string | undefined, enabled = true) {
  return useQuery({
    queryKey: queryKeys.events.chat(id ?? ''),
    enabled: enabled && id !== undefined && id !== null && id !== '',
    staleTime: 10_000,
    refetchInterval: 15_000, // simple polling for live-ish updates
    queryFn: () => api.get<EventChatList>(`events/${id}/chat`),
  });
}

/** POST /api/events/:id/chat/send — send a message. */
export function useSendEventChat() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, text }: { id: number | string; text: string }) =>
      api.post<{ message: EventChatMessage }>(`events/${id}/chat/send`, { text }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: queryKeys.events.chat(vars.id) });
    },
  });
}

/** POST /api/events/:id/invite — creator invites specific friends. */
export function useInviteFriends() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, userIds }: { id: number | string; userIds: number[] }) =>
      api.post<{ invited: number; user_ids: number[] }>(`events/${id}/invite`, {
        user_ids: userIds,
      }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: queryKeys.events.detail(vars.id) });
    },
  });
}

/** POST /api/events/:id/duplicate — clone event with a new starts_at. */
export function useDuplicateEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, startsAt }: { id: number | string; startsAt: string }) =>
      api.post<PlaceEventEnvelope>(`events/${id}/duplicate`, { starts_at: startsAt })
         .then(r => r.event),
    onSuccess: (event: PlaceEvent) => {
      qc.setQueryData(queryKeys.events.detail(event.id), event);
      qc.invalidateQueries({ queryKey: queryKeys.events.all });
    },
  });
}

/** PATCH /api/events/:id with {status: 'C', cancel_reason} — cancel event. */
export function useCancelEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: number | string; reason?: string }) =>
      api.patch<PlaceEventEnvelope>(`events/${id}`, {
        status: 'C',
        cancel_reason: reason || '',
      }).then(r => r.event),
    onSuccess: (event: PlaceEvent) => {
      qc.setQueryData(queryKeys.events.detail(event.id), event);
      qc.invalidateQueries({ queryKey: queryKeys.events.all });
    },
  });
}
// Re-export category union for convenience consumers.
export type { PlaceEventCategory } from '@/types/api';
